import React from 'react';
import { Icon } from '@noths/polaris-design-system-core-components';
import type { Review, ReviewReply } from '@noths/polaris-design-system-ooux-interfaces';

import { ReviewCardPartnerLogo } from '../ReviewCard/ReviewCardPartnerLogo';
import { REPLY_PREFIX, REPLY_SUFFIX } from './copy';
import type { ReviewPreviewCardProps } from './types';
import * as styles from './ReviewPreviewCardReply.styles';

interface ReviewPreviewCardReplyProps {
  href: string;
  onControlClick?: ReviewPreviewCardProps['onControlClick'];
  reply: ReviewReply;
  review: Review;
}

/**
 * The partner's reply, as a call to action rather than as the reply itself.
 *
 * The card is a preview, so it says a reply exists and who wrote it; the panel is where
 * the reply is read. The whole row is one link, and its text is its accessible name, so
 * it announces as "Read Bramble & Co’s reply" with no extra label to keep in step.
 *
 * The logo circle is decorative and absent for a partner who has none, both of which
 * `ReviewCardPartnerLogo` owns: the sentence beside it is what names the partner.
 */
export const ReviewPreviewCardReply = ({
  href,
  onControlClick,
  reply,
  review,
}: ReviewPreviewCardReplyProps) => (
  <a
    css={styles.reply}
    data-testid="review-preview-card-reply"
    href={href}
    onClick={(e) => onControlClick?.(e, { control: 'reply', review })}
  >
    <span css={styles.content}>
      <ReviewCardPartnerLogo logoUrl={reply.partner.logoUrl} size={styles.LOGO_SIZE} />
      <span css={styles.label}>
        {REPLY_PREFIX} <span css={styles.partnerName}>{`${reply.partner.name}’s`}</span>{' '}
        {REPLY_SUFFIX}
      </span>
    </span>
    <Icon aria-hidden css={styles.chevron} name="actionForward" size="md" />
  </a>
);

ReviewPreviewCardReply.displayName = 'ReviewPreviewCardReply';
